/**
 * Firebase Cloud Messaging (FCM) for web push.
 * Requires firebase package and REACT_APP_VAPID_KEY, REACT_APP_FIREBASE_* in env.
 */

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let messaging = null;

async function getMessagingInstance() {
  if (messaging) return messaging;
  if (!VAPID_KEY || !firebaseConfig?.projectId) {
    return null;
  }
  try {
    const { getApp, getApps, initializeApp } = await import("firebase/app");
    const { getMessaging: getFcmMessaging, isSupported } = await import("firebase/messaging");
    if (!getApps().length) {
      initializeApp(firebaseConfig);
    }
    const supported = await isSupported();
    if (!supported) return null;
    messaging = getFcmMessaging(getApp());
    return messaging;
  } catch (e) {
    console.warn("Firebase messaging init failed:", e?.message);
    return null;
  }
}

/**
 * Request notification permission, get FCM token, and return it.
 * @returns {Promise<string|null>} FCM token or null
 */
export async function getFcmToken() {
  const m = await getMessagingInstance();
  if (!m) return null;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;
    const { getToken } = await import("firebase/messaging");
    const token = await getToken(m, { vapidKey: VAPID_KEY });
    return token || null;
  } catch (e) {
    console.warn("FCM getToken failed:", e?.message);
    return null;
  }
}

/**
 * Register FCM token with backend (profile update).
 * Call after login for roles that should receive push (hod, teacher, student).
 */
export async function registerFcmTokenWithBackend(updateProfileFn) {
  if (!updateProfileFn || typeof updateProfileFn !== "function") return;
  const token = await getFcmToken();
  if (!token) return;
  try {
    await updateProfileFn({ fcmToken: token });
  } catch (e) {
    console.warn("Failed to register FCM token:", e?.message);
  }
}
